"use client"

import { useState, useEffect } from "react"
import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { Button } from "@/components/ui/button"
import { Pencil, Trash2 } from "lucide-react"
import { AccountDialog } from "./components/account-dialog"
import { DeleteAccountDialog } from "./components/delete-account-dialog"
import { useAccounts } from "@/src/hooks/useAccounts"
import axios from "@/lib/api/axios-instance"
import toast, { Toaster } from 'react-hot-toast'

export default function AccountsPage() {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingAccountId, setEditingAccountId] = useState<number | undefined>()
  const [branchId, setBranchId] = useState<number | null>(null)
  const [loadingBranch, setLoadingBranch] = useState(true)
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    accountId: number | null
    accountName: string
  }>({
    open: false,
    accountId: null,
    accountName: '',
  })
  const [isDeleting, setIsDeleting] = useState(false)

  // Load first branch on mount (optional)
  useEffect(() => {
    const loadBranch = async () => {
      try {
        setLoadingBranch(true)
        const response = await axios.get('/api/v2/business/branches')
        if (response.data.data && response.data.data.length > 0) {
          setBranchId(response.data.data[0].branch_id)
        }
      } catch (error) {
        console.error('Failed to load branches:', error)
      } finally {
        setLoadingBranch(false)
      }
    }
    loadBranch()
  }, [])

  const { accounts, isLoading, error, refresh, deleteAccount } = useAccounts({
    branchId: branchId || undefined,
    filters: { is_active: true },
    autoLoad: !loadingBranch,
  })

  const handleEdit = (accountId: number) => {
    setEditingAccountId(accountId)
    setIsDialogOpen(true)
  }

  const handleDelete = (account: any) => {
    const accountName = account.account_source === 'bank' 
      ? `${getBankName(account)} ${getAccountNumber(account)}`
      : `${getBankName(account)} ${getAccountNumber(account)}`
    
    setDeleteDialog({
      open: true,
      accountId: account.id,
      accountName,
    })
  }

  const handleConfirmDelete = async () => {
    if (!deleteDialog.accountId) return
    
    setIsDeleting(true)
    
    // Close dialog first
    setDeleteDialog({ open: false, accountId: null, accountName: '' })
    
    try {
      await deleteAccount(deleteDialog.accountId)
      
      // Show success toast
      toast.success('ลบบัญชีสำเร็จ', {
        duration: 3000,
        position: 'top-right',
        icon: '✅',
      })
    } catch (error: any) {
      console.error('Delete account error:', error)
      
      // Show error toast
      if (error.response?.data?.error?.code === 'LAST_ACCOUNT_DELETE') {
        toast.error('ไม่สามารถลบบัญชีสุดท้ายได้', {
          duration: 5000,
          position: 'top-right',
          icon: '⚠️',
          style: {
            background: '#FEE2E2',
            color: '#DC2626',
            border: '1px solid #FCA5A5',
          },
        })
        setTimeout(() => {
          toast('กรุณาเก็บบัญชีไว้อย่างน้อย 1 บัญชีต่อสาขา', {
            duration: 4000,
            position: 'top-right',
            icon: 'ℹ️',
            style: {
              background: '#DBEAFE',
              color: '#1E40AF',
            },
          })
        }, 500)
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error, {
          duration: 4000,
          position: 'top-right',
        })
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message, {
          duration: 4000,
          position: 'top-right',
        })
      } else {
        toast.error('ไม่สามารถลบบัญชีได้ กรุณาลองใหม่อีกครั้ง', {
          duration: 4000,
          position: 'top-right',
        })
      }
      
      // Refresh the list to restore correct state
      refresh()
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDialogClose = () => {
    setIsDialogOpen(false)
    setEditingAccountId(undefined)
  }

  const handleDialogOpen = () => {
    setIsDialogOpen(true)
  }

  const handleSuccess = () => {
    refresh()
    handleDialogClose()
  }

  // Get bank name from config (simplified)
  const getBankName = (account: any) => {
    if (account.account_source === 'bank') {
      // Map bank_code to name (should come from config in real app)
      const bankNames: Record<string, string> = {
        'SCB': 'ไทยพาณิชย์',
        'BBL': 'กรุงเทพ',
        'KBANK': 'กสิกรไทย',
        'KTB': 'กรุงไทย',
        'BAY': 'กรุงศรีอยุธยา',
        'TMB': 'ทหารไทยธนชาต',
        'CIMB': 'ซีไอเอ็มบีไทย',
        'TISCO': 'ทิสโก้',
        'KIATNAKIN': 'เกียรตินาคินภัทร',
        'CITI': 'ซิตี้แบงก์',
        'SCBT': 'สแตนดาร์ดชาร์เตอร์ด',
        'ICBC': 'ไอซีบีซี',
        'GSB': 'ออมสิน',
        'BAAC': 'ธ.ก.ส.',
      }
      return bankNames[account.bank_code] || account.bank_code
    } else if (account.account_source === 'promptpay') {
      return 'พร้อมเพย์'
    } else if (account.account_source === 'shop') {
      return account.shop_name || 'ร้านค้า'
    } else if (account.account_source === 'truemoney') {
      return 'TrueMoney Wallet'
    }
    return '-'
  }

  const getAccountNumber = (account: any) => {
    if (account.account_source === 'bank') {
      return account.account_number
    } else if (account.account_source === 'promptpay') {
      return account.promptpay_id
    } else if (account.account_source === 'shop') {
      return account.shop_code
    } else if (account.account_source === 'truemoney') {
      return account.truemoney_id
    }
    return '-'
  }

  return (
    <DashboardLayout>
      <Toaster />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">บัญชีธนาคาร</h1>
          <div className="flex items-center gap-3">
            <Button 
              className="bg-brand-light-red hover:bg-brand-light-red/90 text-white"
              onClick={handleDialogOpen}
            >
              เพิ่มบัญชีธนาคาร
            </Button>
          </div>
        </div>

        {/* Account Dialog */}
        <AccountDialog 
          open={isDialogOpen} 
          onOpenChange={handleDialogClose}
          branchId={branchId || 0}
          accountId={editingAccountId}
          onSuccess={handleSuccess}
        />

        {/* Branch Selector 
        <div className="mb-6">
          <Button className="bg-brand-blue hover:bg-brand-blue/90 text-white rounded-lg px-6 py-2">สาขาใหม่</Button>
        </div>
        */}

        {/* Section Header */}
        <div className="mb-4">
          <h2 className="text-lg font-normal text-gray-900">
            จัดการบัญชีสาขา <span className="text-brand-blue font-semibold">สาขาใหม่</span>
          </h2>
        </div>

        {/* Accounts Table */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {loadingBranch || isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500">เกิดข้อผิดพลาดในการโหลดข้อมูล</p>
              <Button onClick={refresh} className="mt-4">ลองใหม่อีกครั้ง</Button>
            </div>
          ) : accounts.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">ยังไม่มีบัญชีในระบบ</p>
              <Button 
                onClick={handleDialogOpen} 
                className="mt-4 bg-brand-light-red hover:bg-brand-light-red/90 text-white"
              >
                เพิ่มบัญชีแรก
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">#</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">ประเภท</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">ธนาคาร/แหล่ง</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">เลขที่บัญชี</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">ชื่อบัญชี</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">สถานะ</th>
                    <th className="text-center py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((account, index) => (
                    <tr key={account.id} className="border-b border-gray-100">
                      <td className="py-4 px-4 text-gray-900">{index + 1}</td>
                      <td className="py-4 px-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {account.account_source === 'bank' ? 'ธนาคาร' : 
                           account.account_source === 'promptpay' ? 'พร้อมเพย์' :
                           account.account_source === 'shop' ? 'ร้านค้า' : 'TrueMoney'}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-gray-900">{getBankName(account)}</td>
                      <td className="py-4 px-4 text-gray-900">{getAccountNumber(account)}</td>
                      <td className="py-4 px-4 text-gray-900">{account.account_name}</td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          account.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {account.is_active ? 'ใช้งาน' : 'ปิดใช้งาน'}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-brand-blue hover:text-brand-blue/80 hover:bg-blue-50"
                            onClick={() => handleEdit(account.id)}
                          >
                            <Pencil className="h-5 w-5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-brand-red hover:text-brand-red/80 hover:bg-red-50"
                            onClick={() => handleDelete(account)}
                          >
                            <Trash2 className="h-5 w-5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <DeleteAccountDialog
          open={deleteDialog.open}
          onOpenChange={(open) => {
            if (!isDeleting) {
              setDeleteDialog({ open, accountId: null, accountName: '' })
            }
          }}
          onConfirm={handleConfirmDelete}
          accountName={deleteDialog.accountName}
          isDeleting={isDeleting}
        />
      </div>
    </DashboardLayout>
  )
}
